import {
    ArgoMaterialStatusEnum,
    FetchDefaultLocationDocument,
    GetContextDefaultModelDocument,
    GetMaterialDocument,
    GetWorkOrderDocument,
} from '@redviking/argonaut-core-ui/types/db';
import { accessor } from '../store';
import { messages } from 'src/i18n/i18n';
import { RouterViewSlider } from '../components/RouterViewSlider';
import { RouteEntityDetail } from '@redviking/argonaut-core-ui/src/components/EntityDetail';
import { validateMaterialSelectAcl, validateMaterialUpdateAcl } from './material.validations';
import { entityDetailRoute } from 'src/components/EntityDetail/entity-route-util';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { Notify } from 'src/notifications';
import matClassRoutes from 'src/material-class/material-class.routes';
import { type MaterialEntity, createMaterialEntity, saveMaterialEntity } from './material.entity';
import type { RouteConfig } from 'vue-router';
import { Breadcrumb } from 'src/components/EntityDetail/entity-route-util';

export const materialRoutes: RouteConfig[] = [
    // nav group route
    {
        path: '/material',
        name: 'matNav',
        component: RouterViewSlider,
        redirect: { name: 'materialSearch' },
        meta: {
            navigationGuard: () => validateMaterialSelectAcl(),
            navbarTitle: messages.entityNamesPlural.material,
            navbarGroupOnly: true,
            transition: 'vertical',
            featureFlags: [],
        },
        children: [
            {
                name: 'materialSearch',
                path: 'search',
                component: () => import(/* webpackChunkName: "material" */ './MaterialSearch.vue'),
                meta: {
                    transition: 'horizontal',
                    navbarIcon: 'mdi-magnify',
                    navbarTitle: 'Material Search',
                    pageBannerTitle: 'Material Search',
                    navigationGuard: validateMaterialSelectAcl,
                    pageBannerSubtitle: 'Search for a desired Material',
                },
            },
            ...matClassRoutes,
            entityDetailRoute<'material'>({
                path: '/material/:materialId',
                name: 'materialMaintenance',
                component: RouteEntityDetail,
                redirect: to => ({ name: 'materialDetail', params: to.params }),
                meta: {
                    entityType: 'material',
                    canEdit: validateMaterialUpdateAcl,
                    useEntityDetailTabs: (to) => to.name !== 'materialCreate',
                    pageBannerTitle: (to) => {
                        const materialEntity = accessor.entityAsType('material');
                        if (!materialEntity) {
                            return 'Material';
                        }
                        let serialNumber: string;
                        if (to.query.mode === 'create') {
                            if (materialEntity.snCfg.kind === 'direct' && materialEntity.snCfg.serialNumber) {
                                serialNumber = materialEntity.snCfg.serialNumber;
                            } else {
                                serialNumber = 'New Material';
                            }
                        } else if (materialEntity.snCfg.kind === 'direct') {
                            serialNumber = materialEntity.snCfg.serialNumber;
                        } else {
                            throw new Error('Serial number should have been fetched as a string since the material has already been created');
                        }

                        const materialClass = materialEntity.materialClass;
                        const materialModel = materialEntity.materialModel;

                        const breadcrumb: Breadcrumb[] = [
                            {
                                disabled: false,
                                text: materialClass.name,
                                to: {
                                    name: 'materialClassMaintenence',
                                    params: {
                                        materialClassId: materialClass.id,
                                    },
                                },
                            },
                            {
                                disabled: false,
                                text: materialModel.model_number,
                                to: {
                                    name: 'materialClassMaintenence',
                                    params: {
                                        materialClassId: materialClass.id,
                                    },
                                },
                            },
                            {
                                disabled: true,
                                text: serialNumber,
                                to: { name: 'materialSearch' },
                            },
                        ];
                        return breadcrumb;
                    },
                    getEntity: async (to) => {
                        if (to.query.mode === 'create') {
                            const {
                                argo_location: defaultLocationData,
                            } = await gqlClient.request({
                                document: FetchDefaultLocationDocument,
                                variables: {
                                    contextId: accessor.auth.contextId,
                                },
                            });

                            const defaultEntity: MaterialEntity = {
                                // NOTE: Since we are creating a material the id does not matter. We rely on the database to generate the id
                                id: '',
                                udfData: [],
                                // NOTE: Same as above
                                createdBy: '',
                                enabled: true,
                                attributeIds: [],
                                workOrderId: null,
                                ignoreSchemaWarning: false,
                                status: ArgoMaterialStatusEnum.None,
                                locationId: defaultLocationData && defaultLocationData.length === 1 ? defaultLocationData[0].id : null,
                                snCfg: {
                                    kind: 'direct',
                                    serialNumber: '',
                                },
                                materialClass: {
                                    id: '',
                                    name: '',
                                },
                                materialModel: {
                                    id: '',
                                    model_number: '',
                                },
                            };

                            const workOrderId = typeof to.query.workOrderId === 'string' ? to.query.workOrderId : null;
                            const matModId = typeof to.query.materialModelId === 'string' ? to.query.materialModelId : null;

                            if (workOrderId) {
                                const {
                                    workOrder,
                                } = await gqlClient.request({
                                    document: GetWorkOrderDocument,
                                    variables: {
                                        work_order_id: workOrderId,
                                    },
                                });
                                if (!workOrder) {
                                    const e = new Error('No work order found for the provided workOrderId.');
                                    Notify.error(e);
                                    throw e;
                                }
                                defaultEntity.workOrderId = workOrder.id;
                                defaultEntity.materialModel.id = workOrder.material_model.id;
                            } else if (matModId) {
                                defaultEntity.materialModel.id = matModId;
                            } else {
                                const {
                                    argo_material_model: defaultModelNumberData,
                                } = await gqlClient.request({
                                    document: GetContextDefaultModelDocument,
                                    variables: {
                                        contextId: accessor.auth.contextId,
                                    },
                                });
                                const defaultModelNumber = defaultModelNumberData[0];
                                if (!defaultModelNumber) {
                                    const e = new Error('No default model number found for the current context.');
                                    Notify.error(e);
                                    throw e;
                                }
                                defaultEntity.materialModel.id = defaultModelNumber.id;
                            }
                            return {
                                entity: defaultEntity,
                            };
                        }
                        try {
                            const {
                                material: materialData,
                            } = await gqlClient.request({
                                document: GetMaterialDocument,
                                variables: {
                                    materialId: to.params.materialId,
                                },
                            });
                            if (!materialData) {
                                const e = new Error('No material found for the provided materialId.');
                                Notify.error(e);
                                throw e;
                            }
                            const ret: MaterialEntity = {
                                snCfg: {
                                    kind: 'direct',
                                    serialNumber: materialData.serial_number,
                                },
                                id: materialData.id,
                                enabled: materialData.enabled,
                                udfData: materialData.udfData,
                                created_at: materialData.created_at,
                                status: materialData.status,
                                createdBy: materialData.created_by.username,
                                locationId: materialData.created_location?.id || null,
                                attributeIds: materialData.materialAttributes.map(a => a.attribute.id),
                                materialClass: {
                                    id: materialData.material_model.material_class.id,
                                    name: materialData.material_model.material_class.name,
                                },
                                materialModel: {
                                    id: materialData.material_model.id,
                                    model_number: materialData.material_model.model_number,
                                },
                                ignoreSchemaWarning: false,
                                workOrderId: materialData?.workOrderMaterial?.workOrderId || null,
                            };
                            return {
                                entity: ret,
                            };
                        } catch (e) {
                            Notify.error(e);
                            throw e;
                        }
                    },
                    saveEntity: saveMaterialEntity,
                    createEntity: createMaterialEntity,
                },
                children: [
                    {
                        path: 'create',
                        name: 'materialCreate',
                        component: () => import(/* webpackChunkName: "material" */ './MaterialCreate.view.vue'),
                    },
                    {
                        path: 'detail',
                        name: 'materialDetail',
                        component: () => import(/* webpackChunkName: "material" */ './MaterialProcessReport.view.vue'),
                        meta: {
                            tab: {
                                label: 'Detail',
                                icon: 'mdi-chart-line-stacked',
                            },
                            pageBannerSubtitle: 'Material Process Status Report',
                        },
                    },
                    {
                        path: 'material-attribute-history',
                        name: 'materialAttributeHistory',
                        component: () => import(/* webpackChunkName: "material" */ './MaterialAttributeHistory.view.vue'),
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Attribute History',
                                icon: 'mdi-history',
                            },
                            navigationGuard: () => validateMaterialSelectAcl(),
                            pageBannerSubtitle: () => {
                                return 'See a timeline of what attributes were linked to or removed from this material';
                            },
                        },
                    },
                    {
                        path: 'material-work-instruction-history',
                        name: 'materialWorkInstructionHistory',
                        component: () => import(/* webpackChunkName: "material" */ './MaterialWorkInstructionHistory.view.vue'),
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Work Instruction History',
                                icon: 'mdi-history',
                            },
                            navigationGuard: () => validateMaterialSelectAcl(),
                            pageBannerSubtitle: () => {
                                return 'See a timeline of what work instructions were linked to this material and their progress';
                            },
                        },
                    },
                ],
            }),
        ],
    },
];
